// TODO: Lots of param-reassigns in this file, consider refactoring

import type { AreaContourBase } from '../contours/area-contour-base';
import type { ImageIndices } from '../dicom-web/current-study-store';

import { InitViewIDs } from '@src/config';
import { defineStore } from 'pinia';

import { useAlert } from '@/src/composables/useAlert';

import { useContourContainerStore } from '../contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '../contours/contour-definitions';

import { useViewportStateStore } from '../contours/viewport-state-store';
import {
  useCurrentStudyStore,
  VencDirection2D,
} from '../dicom-web/current-study-store';

export interface FlowAnalysisState {
  TotalForwardVolume: number;
  TotalBackwardVolume: number;
  TotalVolume: number;
  RegurgitationFraction: number;
  VolPerMin: number;
  VolPerMinEffective: number;
  RulerRange: { left: number; right: number; delta: number };
  RulerForwardVolume: number;
  RulerBackwardVolume: number;
  RulerTotalVolume: number;
  HeartRate: number;
  MaxPressureGradient: number;
  MeanPressureGradient: number;
  MaximumVelocity: number;
  MinimumVelocity: number;
  MaximumAcceleration: number;
  MinimumAcceleration: number;
  MaximumFlow: number;
  MinimumFlow: number;
  TriggerTimes: number[];
  FlowRates: number[];
  SumSignalIntensities: number[];
  MeanSignalIntensities: number[];
  StdDevSignalIntensities: number[];
  MaxSignalIntensities: number[];
  MinSignalIntensities: number[];
  Areas: number[];
  Antialiasing: boolean;
  StaticTissueRoiCorrection: boolean;
  StaticTissueMaskCorrection: boolean;
  PhantomCorrection: boolean;
  MagnitudeMask: boolean;
  InvertedFlow: boolean;

  seriesIndices: ImageIndices;
}

export function getDefaultFlowAnalysisState(): FlowAnalysisState {
  return {
    TotalForwardVolume: 0,
    TotalBackwardVolume: 0,
    TotalVolume: 0,
    RegurgitationFraction: 0,
    VolPerMin: 0,
    VolPerMinEffective: 0,
    RulerRange: { left: 0, right: 0, delta: 0 },
    RulerForwardVolume: 0,
    RulerBackwardVolume: 0,
    RulerTotalVolume: 0,
    HeartRate: 0,
    MaxPressureGradient: 0,
    MeanPressureGradient: 0,
    MaximumVelocity: 0,
    MinimumVelocity: 0,
    MaximumAcceleration: 0,
    MinimumAcceleration: 0,
    MaximumFlow: 0,
    MinimumFlow: 0,
    TriggerTimes: [],
    FlowRates: [],
    SumSignalIntensities: [],
    MeanSignalIntensities: [],
    StdDevSignalIntensities: [],
    MaxSignalIntensities: [],
    MinSignalIntensities: [],
    Areas: [],
    Antialiasing: false,
    StaticTissueRoiCorrection: false,
    StaticTissueMaskCorrection: false,
    PhantomCorrection: false,
    MagnitudeMask: false,
    InvertedFlow: false,

    seriesIndices: { seriesIdx: -1, sliceIdx: -1, imageIdx: -1 },
  };
}

// integration helpers
interface SamplePoint {
  t: number;
  f: number;
}

function lerp(a: number, b: number, alpha: number) {
  return a + (b - a) * alpha;
}

function splitSegment(
  p0: SamplePoint,
  p1: SamplePoint,
  rulerStart: number,
  rulerEnd: number,
) {
  const result = [p0, p1];

  if (p0.f * p1.f < 0) {
    const alpha = p0.f / (p0.f - p1.f);
    result.push({ t: lerp(p0.t, p1.t, alpha), f: 0 });
  }

  [rulerStart, rulerEnd].forEach((rulerTime) => {
    if (
      Number.isFinite(rulerTime)
      && rulerTime > Math.min(p0.t, p1.t)
      && rulerTime < Math.max(p0.t, p1.t)
    ) {
      const beta = (rulerTime - p0.t) / (p1.t - p0.t);
      result.push({ t: rulerTime, f: lerp(p0.f, p1.f, beta) });
    }
  });

  return result.sort((a, b) => a.t - b.t);
}

function integrateSegments(
  triggerTimes: number[],
  flowRates: number[],
  rulerRange: { left: number; right: number },
) {
  let forward = 0;
  let backward = 0;
  let rulerForward = 0;
  let rulerBackward = 0;
  let rulerTotal = 0;

  const rulerStart = rulerRange.left;
  const rulerEnd = rulerRange.right;

  const accumulate = (
    p0: SamplePoint,
    p1: SamplePoint,
    withinRuler: boolean,
  ) => {
    const dt = (p1.t - p0.t) / 1000; // ms → s
    if (dt <= 0) return;
    const avg = (p0.f + p1.f) / 2;
    const volume = avg * dt;

    if (volume >= 0) {
      forward += volume;
      if (withinRuler) {
        rulerForward += volume;
        rulerTotal += volume;
      }
    }
    else {
      backward += Math.abs(volume);
      if (withinRuler) {
        rulerBackward += Math.abs(volume);
        rulerTotal += volume;
      }
    }
  };

  const segmentCount = flowRates.length;
  for (let i = 0; i < segmentCount - 1; i++) {
    const augmented = splitSegment(
      { t: triggerTimes[i], f: flowRates[i] },
      { t: triggerTimes[i + 1], f: flowRates[i + 1] },
      rulerStart,
      rulerEnd,
    );

    for (let j = 0; j < augmented.length - 1; j++) {
      const p0 = augmented[j];
      const p1 = augmented[j + 1];
      const midTime = (p0.t + p1.t) / 2;
      const withinRuler = midTime >= rulerStart && midTime <= rulerEnd;
      accumulate(p0, p1, withinRuler);
    }
  }

  return {
    forward,
    backward,
    rulerForward,
    rulerBackward,
    rulerTotal,
  };
}

// Flow ROIs that produce analysis results. Static-tissue is not a flow ROI —
// it is only sampled as a background reference when staticTissueRoiCorrection
// is enabled (see calculateRoi). Including it here made Object.values() of
// flowAnalysisStates treat it as "Flow ROI 2" after forwarding.
const flowContourLabels = [
  ContourLabel.FlowRoi1,
  ContourLabel.FlowRoi2,
] as const;

export const useFlowCalculatorStore = defineStore('flowCalculatorStore', {
  state: () => ({
    flowAnalysisStates: {} as Record<ContourLabel, FlowAnalysisState>,

    staticTissueRoiCorrection: false,
    staticTissueMaskCorrection: false,
    phantomCorrection: false,
    // Phantom series index used when phantomCorrection is enabled. Persisted in
    // the store so leaving/re-entering the flow component can re-apply the
    // correction after the shared image cache is restored to original.
    phantomSeriesIdx: -1,
    antialiasing: false,

    rulerRange: { left: 0, right: 0, delta: 0 },

    seriesIndices: { seriesIdx: -1, sliceIdx: -1, imageIdx: -1 },
    invertedFlowRois: [] as ContourLabel[],

    overlayNoiseThreshold: 15,

    // GE venc series currently magnitude-normalized in the shared image cache,
    // mapped to the velocity UIDs that were swapped. Owned here (not in the
    // component) so this base correction layer survives component remounts and
    // is the single source of truth for "is this series normalized".
    geNormalizedSeries: new Map<string, string[]>(),

    // Last series (by UID) we already warned about not being through-plane, so
    // the "flow needs through-plane encoding" warning fires once per series
    // rather than on every recalc.
    lastWarnedVencSeriesUid: null as string | null,
  }),
  actions: {
    updateSeriesIndices() {
      this.seriesIndices = useViewportStateStore().getSelectedImageIndices(
        InitViewIDs.FlowPhase,
      );
    },
    async calculate() {
      this.seriesIndices = useViewportStateStore().getSelectedImageIndices(
        InitViewIDs.FlowPhase,
      );

      if (this.seriesIndices.seriesIdx < 0 || this.seriesIndices.sliceIdx < 0) {
        this.flowAnalysisStates = {} as typeof this.flowAnalysisStates;
        return;
      }

      // Flow quantification is only valid for through-plane velocity encoding.
      // For in-plane / speed / unknown directions we don't calculate any values
      // — clear results and warn once per series.
      if (!this.isThroughPlaneSeries(this.seriesIndices.seriesIdx)) {
        this.flowAnalysisStates = {} as typeof this.flowAnalysisStates;
        const seriesUid = useCurrentStudyStore().getSeriesUID(
          this.seriesIndices.seriesIdx,
        );
        if (this.lastWarnedVencSeriesUid !== seriesUid) {
          useAlert().warning(
            'Flow can only be calculated for through-plane velocity encoded series.',
          );
          this.lastWarnedVencSeriesUid = seriesUid;
        }
        return;
      }
      this.lastWarnedVencSeriesUid = null;

      // Re-validate static-tissue ROI correction on every recalc: it needs a
      // static tissue ROI on every phase. If that's no longer the case (e.g. an
      // ROI was deleted after enabling), auto-disable it and warn once so a
      // stale/invalid correction never lingers.
      if (
        this.staticTissueRoiCorrection
        && !this.hasStaticTissueRoiInAllPhases(this.seriesIndices.seriesIdx)
      ) {
        this.staticTissueRoiCorrection = false;
        useAlert().warning(
          'Static Tissue ROI correction disabled: a static tissue ROI is missing on one or more phases.',
        );
      }

      this.flowAnalysisStates = {} as typeof this.flowAnalysisStates;
      const results = await Promise.all(
        flowContourLabels.map(async contourLabel =>
          this.calculateRoi(contourLabel),
        ),
      );
      results.forEach((analysisState, idx) => {
        if (analysisState.TriggerTimes.length > 0) {
          this.flowAnalysisStates[flowContourLabels[idx]] = analysisState;
        }
      });
    },
    resetContourSignalIntensityStatistic(seriesIdx?: number) {
      const currentStudyStore = useCurrentStudyStore();
      const idx = seriesIdx ?? this.seriesIndices.seriesIdx;
      if (idx < 0) return;

      const series = currentStudyStore.activeStudy.series[idx];
      if (!series?.slices?.[0]?.images) return;

      this.resetContourSignalIntensityStatisticForUids(
        series.slices[0].images.map(image => image.uid),
      );
    },
    resetContourSignalIntensityStatisticForUids(velUids: string[]) {
      const contourStore = useContourContainerStore();
      velUids.forEach((uid) => {
        const container = contourStore.getContourContainer(uid);
        if (!container) return;
        const allContours = container.getDrawingContours() || {};
        Object.keys(allContours).forEach((label) => {
          const contour = container.getContour({
            label: label as ContourLabel,
            id: encodeContourLabelItemId([0]),
          }) as AreaContourBase;
          if (contour) {
            contour.resetSignalIntensityStatistic();
          }
        });
      });
    },
    // ─── GE magnitude normalization (base correction layer) ──────────────────
    // GE venc special case: when the phase/velocity image is magnitude-masked,
    // each phase voxel must be divided by the corresponding magnitude voxel
    // before the flow calc AND for display. Applied automatically (not a user
    // option) as the base correction layer that every other correction composes
    // on top of. Built from the *pristine original* phase data so it can never
    // divide twice; magnitude voxels that are 0 are left untouched.
    isThroughPlaneSeries(seriesIdx?: number): boolean {
      const idx = seriesIdx ?? this.seriesIndices.seriesIdx;
      if (idx < 0) return false;
      const store = useCurrentStudyStore();
      const velUid
        = store.activeStudy.series[idx]?.slices?.[0]?.images?.[0]?.uid;
      if (!velUid) return false;
      return (
        store.imageInfoDicom[velUid]?.VencDirection2D
        === VencDirection2D.ThroughPlane
      );
    },
    isGeMagnitudeMaskedSeries(seriesIdx?: number): boolean {
      const idx = seriesIdx ?? this.seriesIndices.seriesIdx;
      if (idx < 0) return false;
      const store = useCurrentStudyStore();
      const velUid
        = store.activeStudy.series[idx]?.slices?.[0]?.images?.[0]?.uid;
      if (!velUid) return false;
      const info = store.imageInfoDicom[velUid];
      return !!info?.IsVencImage2D && !!info?.IsMagnitudeMasked;
    },
    async applyGeMagnitudeNormalization(
      seriesIdx?: number,
    ): Promise<boolean> {
      const store = useCurrentStudyStore();
      const idx = seriesIdx ?? this.seriesIndices.seriesIdx;
      if (idx < 0) return false;

      const series = store.activeStudy.series[idx];
      const velUids = series?.slices?.[0]?.images?.map(img => img.uid) ?? [];
      const magUids = series?.slices?.[1]?.images?.map(img => img.uid) ?? [];
      if (!velUids.length || !this.isGeMagnitudeMaskedSeries(idx)) {
        return false;
      }

      const seriesUid = store.getSeriesUID(idx);
      if (this.geNormalizedSeries.has(seriesUid)) return false; // already applied

      if (velUids.length !== magUids.length) {
        console.warn(
          `GE magnitude normalization skipped: phase/magnitude frame count mismatch (${velUids.length} vs ${magUids.length})`,
        );
        return false;
      }

      // ensure both phase and magnitude frames are loaded before dividing
      const missingUids = [...velUids, ...magUids].filter(
        uid => !store.isImageAvailable(uid),
      );
      if (missingUids.length) {
        await Promise.all(
          missingUids.map(async uid => store.fetchOrGetVtkImageData(uid)),
        );
      }

      velUids.forEach((velUid, frameIndex) => {
        const magUid = magUids[frameIndex];

        // Divide the *pristine original* phase by the magnitude so the divide
        // is never applied on top of an already-normalized frame
        // (modifyVtkImageData replaces the scalars, so writing the freshly
        // divided original is safe and idempotent).
        const velImageData
          = store.originalVtkImageData?.[velUid]?.originalData
            ?? store.getVtkImageData(velUid);
        const magImageData = store.getVtkImageData(magUid);
        if (!velImageData || !magImageData) {
          console.warn(
            `GE magnitude normalization: missing image data for frame ${frameIndex}`,
          );
          return;
        }

        const velData = velImageData.getPointData().getScalars().getData();
        const magData = magImageData.getPointData().getScalars().getData();

        if (velData.length !== magData.length) {
          console.warn(
            `GE magnitude normalization: pixel count mismatch at frame ${frameIndex} (${velData.length} vs ${magData.length})`,
          );
          return;
        }

        const corrected = new Float32Array(velData.length);
        for (let i = 0; i < velData.length; i++) {
          // divide by the pure magnitude SI; magnitude 0 => leave as-is
          corrected[i] = magData[i] === 0 ? velData[i] : velData[i] / magData[i];
        }

        const success = store.modifyVtkImageData(
          velUid,
          corrected,
          `GE magnitude normalization (frame ${frameIndex})`,
        );

        if (!success) {
          console.warn(
            `GE magnitude normalization: failed to update image ${velUid}`,
          );
        }
      });

      this.geNormalizedSeries.set(seriesUid, velUids);
      this.resetContourSignalIntensityStatistic(idx);
      console.log(
        `GE magnitude normalization applied to ${velUids.length} frames`,
      );
      return true;
    },
    // Restore a single normalized series' velocity images to their original.
    restoreGeMagnitudeNormalization(seriesUid: string): boolean {
      const velUids = this.geNormalizedSeries.get(seriesUid);
      if (!velUids) return false;
      useCurrentStudyStore().resetModifiedVtkImageData(velUids);
      this.geNormalizedSeries.delete(seriesUid);
      this.resetContourSignalIntensityStatisticForUids(velUids);
      return true;
    },
    // Restore every normalized series so the shared image cache is handed back
    // exactly as it was found once we leave the flow context. Also resets the
    // current flow series' velocity frames when phantom (or other) corrections
    // modified them without a GE normalization entry.
    restoreAllGeMagnitudeNormalization(): boolean {
      const store = useCurrentStudyStore();
      let restored = false;

      if (this.geNormalizedSeries.size > 0) {
        [...this.geNormalizedSeries.keys()].forEach(seriesUid =>
          this.restoreGeMagnitudeNormalization(seriesUid),
        );
        restored = true;
      }

      // Phantom (and similar) corrections may have modified the current series
      // even when GE magnitude normalization was not applicable — still hand
      // back the original scalars when leaving flow.
      const { seriesIdx } = this.seriesIndices;
      if (seriesIdx >= 0) {
        const velUids
          = store.activeStudy.series[seriesIdx]?.slices?.[0]?.images?.map(
            img => img.uid,
          ) ?? [];
        if (velUids.length) {
          const stillModified = velUids.some(
            uid => !!store.originalVtkImageData?.[uid],
          );
          if (stillModified) {
            store.resetModifiedVtkImageData(velUids);
            this.resetContourSignalIntensityStatisticForUids(velUids);
            restored = true;
          }
        }
      }

      return restored;
    },
    // Reset a flow series' velocity data to its baseline: the original image
    // with the mandatory GE magnitude normalization re-applied on top. The
    // other corrections reset to the original via resetModifiedVtkImageData when
    // toggled off; routing those resets through here guarantees turning one
    // correction off never silently drops the GE normalization base layer.
    async resetFlowVelocityToBaseline(seriesIdx?: number): Promise<void> {
      const store = useCurrentStudyStore();
      const idx = seriesIdx ?? this.seriesIndices.seriesIdx;
      if (idx < 0) return;

      const series = store.activeStudy.series[idx];
      const velUids = series?.slices?.[0]?.images?.map(img => img.uid) ?? [];
      if (!velUids.length) return;

      const seriesUid = store.getSeriesUID(idx);
      store.resetModifiedVtkImageData(velUids);
      this.geNormalizedSeries.delete(seriesUid);

      // re-establish the base layer (no-op if not applicable); it resets the
      // contour SI cache itself, but reset again to cover the not-applicable
      // path where the data was just restored to the original.
      await this.applyGeMagnitudeNormalization(idx);
      this.resetContourSignalIntensityStatistic(idx);
    },
    // ─── Phantom background correction ───────────────────────────────────────
    // Subtracts a phantom acquisition (background phase) frame-by-frame from the
    // flow velocity data. Unlike GE normalization this composes on top of the
    // *current* cache (so it stacks on the GE base layer when present), rather
    // than rebuilding from the pristine original. Toggling it off resets to the
    // baseline via resetFlowVelocityToBaseline (which re-applies the GE base).
    async applyPhantomCorrection(
      seriesIdx: number,
      phantomSeriesIdx: number,
    ): Promise<boolean> {
      const store = useCurrentStudyStore();
      if (seriesIdx < 0 || phantomSeriesIdx < 0) return false;

      const flowSeries = store.activeStudy.series[seriesIdx];
      const velUids = flowSeries?.slices?.[0]?.images?.map(img => img.uid) ?? [];
      if (!velUids.length) return false;

      const phantomSeries = store.activeStudy.series[phantomSeriesIdx];
      const phantomUids
        = phantomSeries?.slices?.[0]?.images?.map(img => img.uid) ?? [];
      if (!phantomUids.length) {
        console.warn('Phantom correction: phantom series has no images');
        return false;
      }

      if (velUids.length !== phantomUids.length) {
        console.warn(
          `Phantom correction: phase count mismatch (flow ${velUids.length} vs phantom ${phantomUids.length})`,
        );
        return false;
      }

      // ensure both flow and phantom frames are loaded before subtracting
      const missingUids = [...velUids, ...phantomUids].filter(
        uid => !store.isImageAvailable(uid),
      );
      if (missingUids.length) {
        await Promise.all(
          missingUids.map(async uid => store.fetchOrGetVtkImageData(uid)),
        );
      }

      velUids.forEach((velUid, frameIndex) => {
        const phantomUid = phantomUids[frameIndex];

        // compose on the current cache so the GE base layer (if any) is kept
        const velImageData = store.getVtkImageData(velUid);
        const phantomImageData = store.getVtkImageData(phantomUid);
        if (!velImageData || !phantomImageData) {
          console.warn(
            `Phantom correction: missing image data for frame ${frameIndex}`,
          );
          return;
        }

        const velData = velImageData.getPointData().getScalars().getData();
        const phantomData = phantomImageData
          .getPointData()
          .getScalars()
          .getData();

        if (velData.length !== phantomData.length) {
          console.warn(
            `Phantom correction: pixel count mismatch at frame ${frameIndex} (${velData.length} vs ${phantomData.length})`,
          );
          return;
        }

        const corrected = new Float32Array(velData.length);
        for (let i = 0; i < velData.length; i++) {
          corrected[i] = velData[i] - phantomData[i];
        }

        const success = store.modifyVtkImageData(
          velUid,
          corrected,
          `Phantom correction applied (frame ${frameIndex})`,
        );

        if (!success) {
          console.warn(
            `Phantom correction: failed to update image ${velUid}`,
          );
        }
      });

      this.resetContourSignalIntensityStatistic(seriesIdx);
      this.phantomCorrection = true;
      this.phantomSeriesIdx = phantomSeriesIdx;
      console.log(
        `Updated ${velUids.length} frames with phantom-subtracted velocity data`,
      );
      return true;
    },
    clearPhantomCorrection() {
      this.phantomCorrection = false;
      this.phantomSeriesIdx = -1;
    },
    // Static-tissue (background) ROI correction requires a static tissue ROI on
    // every phase of the flow series. Returns false if any phase is missing one
    // (no container, no contour, or a degenerate polygon).
    hasStaticTissueRoiInAllPhases(seriesIdx?: number): boolean {
      const store = useCurrentStudyStore();
      const idx = seriesIdx ?? this.seriesIndices.seriesIdx;
      if (idx < 0) return false;

      const images = store.activeStudy.series[idx]?.slices?.[0]?.images;
      if (!images?.length) return false;

      const contourStore = useContourContainerStore();
      return images.every((image) => {
        const container = contourStore.getContourContainer(image.uid);
        if (!container) return false;
        const contour = container.getContour({
          label: ContourLabel.FlowStaticTissueRoi,
          id: encodeContourLabelItemId([0]),
        }) as AreaContourBase | undefined;
        return !!contour && contour.getPolygon().length >= 3;
      });
    },
    async calculateRoi(contourLabel: ContourLabel) {
      const store = useCurrentStudyStore();
      const series = store.activeStudy.series[this.seriesIndices.seriesIdx];
      if (!series?.slices?.[0]?.images) {
        return getDefaultFlowAnalysisState();
      }
      const phaseCount = series.slices[0].images.length;
      if (phaseCount === 0) return getDefaultFlowAnalysisState();

      const analysisState = getDefaultFlowAnalysisState();
      analysisState.seriesIndices = this.seriesIndices;
      analysisState.RulerRange = this.rulerRange;

      let inversionScale = 1;
      analysisState.MagnitudeMask = this.isGeMagnitudeMaskedSeries(
        this.seriesIndices.seriesIdx,
      );
      analysisState.InvertedFlow = this.invertedFlowRois.includes(contourLabel);
      if (analysisState.InvertedFlow) {
        inversionScale = -1;
      }

      // Static-tissue (background) ROI correction applies to the flow ROIs only,
      // never to the static ROI itself. Record on the state whether it was used.
      const applyBgCorrection
        = this.staticTissueRoiCorrection
          && contourLabel !== ContourLabel.FlowStaticTissueRoi;
      analysisState.StaticTissueRoiCorrection = applyBgCorrection;

      // ensure all images are loaded
      const { images } = series.slices[0];
      const missingUIDs = images
        .map(img => img.uid)
        .filter(uid => !store.isImageAvailable(uid));
      if (missingUIDs.length > 0) {
        await Promise.all(
          missingUIDs.map(async uid => store.fetchOrGetVtkImageData(uid)),
        );
      }

      const imageHeartRates = images.map(
        img =>
          useCurrentStudyStore().imageInfoDicom[img.uid]?.CalculatedHeartRate,
      );
      const validRates = imageHeartRates.filter(
        (rate): rate is number => rate !== undefined && rate !== 0,
      );
      const heartRate
        = validRates.length > 0
          ? validRates.reduce((sum, r) => sum + r, 0) / validRates.length
          : 0;

      for (let i = 0; i < phaseCount; i++) {
        const UID = series.slices[0].images[i].uid;

        const info = useCurrentStudyStore().imageInfoDicom[UID];
        const pixelSpacing = info?.PixelSpacing;
        if (
          info
          && pixelSpacing
          && pixelSpacing[0] !== 0
          && pixelSpacing[1] !== 0
        ) {
          const triggerTime = info.TriggerTime || 0;
          const pixelArea = pixelSpacing[0] * pixelSpacing[1];
          const contours = useContourContainerStore().getContourContainer(UID);
          if (contours) {
            const contour = contours.getContour({
              label: contourLabel,
              id: encodeContourLabelItemId([0]),
            }) as AreaContourBase;
            if (contour) {
              const area = contour.getAreaRealWorldvalue();

              // Background correction: the static tissue ROI's mean velocity is a
              // constant background offset. Subtract it (per pixel) from the
              // mean/max/min, and subtract the same offset over this ROI's area
              // from the raw sum (== SI-per-area × area). Done on the raw values
              // before inversion; std dev is unaffected by a constant offset.
              let bgOffsetPerPixel = 0;
              let bgSumCorrection = 0;
              if (applyBgCorrection) {
                const staticContour = contours.getContour({
                  label: ContourLabel.FlowStaticTissueRoi,
                  id: encodeContourLabelItemId([0]),
                }) as AreaContourBase | undefined;
                const staticArea = staticContour?.getAreaRealWorldvalue() ?? 0;
                if (staticContour && staticArea > 0) {
                  const staticSiPerArea
                    = staticContour.getSumSignalIntensity() / staticArea;
                  bgOffsetPerPixel = staticContour.getMeanSignalIntensity();
                  bgSumCorrection = staticSiPerArea * area;
                }
              }

              const sumSignalIntensity
                = (contour.getSumSignalIntensity() - bgSumCorrection)
                  * inversionScale;
              const meanSignalIntensity
                = (contour.getMeanSignalIntensity() - bgOffsetPerPixel)
                  * inversionScale;
              const stdDevSignalIntensity = contour.getStdvSignalIntensity();
              const maxFullPixel
                = contour.getMaxSignalIntensityFullPixel() - bgOffsetPerPixel;
              const minFullPixel
                = contour.getMinSignalIntensityFullPixel() - bgOffsetPerPixel;
              const maxSignalIntensity
                = (inversionScale === -1 ? minFullPixel : maxFullPixel)
                  * inversionScale;
              const minSignalIntensity
                = (inversionScale === -1 ? maxFullPixel : minFullPixel)
                  * inversionScale;
              analysisState.TriggerTimes.push(triggerTime);
              analysisState.SumSignalIntensities.push(sumSignalIntensity);
              analysisState.FlowRates.push(
                (pixelArea / 100) * sumSignalIntensity,
              );
              analysisState.MeanSignalIntensities.push(meanSignalIntensity);
              analysisState.StdDevSignalIntensities.push(stdDevSignalIntensity);
              analysisState.MaxSignalIntensities.push(maxSignalIntensity);
              analysisState.MinSignalIntensities.push(minSignalIntensity);
              analysisState.Areas.push(area);
            }
          }
        }
      }

      if (analysisState.MaxSignalIntensities.length > 0) {
        analysisState.MaximumVelocity = Math.max(
          ...analysisState.MaxSignalIntensities,
        );
        analysisState.MinimumVelocity = Math.min(
          ...analysisState.MinSignalIntensities,
        );
      }

      if (analysisState.TriggerTimes.length > 1) {
        const { forward, backward, rulerForward, rulerBackward, rulerTotal }
          = integrateSegments(
            analysisState.TriggerTimes,
            analysisState.FlowRates,
            this.rulerRange,
          );

        analysisState.TotalForwardVolume = forward;
        analysisState.TotalBackwardVolume = backward;
        analysisState.TotalVolume = forward - backward;

        const absForward = Math.abs(forward);
        const absBackward = Math.abs(backward);
        const forwardDominant = absForward >= absBackward;
        const dominantVolume = Math.max(absForward, absBackward);
        const regurgitantVolume = Math.min(absForward, absBackward);
        analysisState.RegurgitationFraction
          = dominantVolume > 0 ? (regurgitantVolume / dominantVolume) * 100 : 0;

        analysisState.RulerForwardVolume = rulerForward;
        analysisState.RulerBackwardVolume = rulerBackward;
        analysisState.RulerTotalVolume = rulerTotal;

        analysisState.MaximumFlow = Math.max(...analysisState.FlowRates);
        analysisState.MinimumFlow = Math.min(...analysisState.FlowRates);

        if (forwardDominant) {
          const peakVelocity = analysisState.MaximumVelocity;
          analysisState.MaxPressureGradient
            = (((4 * peakVelocity) / 100) * peakVelocity) / 100;

          const positiveMax = analysisState.MaxSignalIntensities.filter(
            v => v > 0,
          );
          if (positiveMax.length === 0) {
            analysisState.MeanPressureGradient = 0;
          }
          else {
            const meanVelocitySquared
              = positiveMax.reduce((sum, v) => sum + v * v, 0)
                / positiveMax.length;
            analysisState.MeanPressureGradient
              = (4 * meanVelocitySquared) / 100 / 100;
          }
        }
        else {
          const peakVelocity = analysisState.MinimumVelocity;
          analysisState.MaxPressureGradient
            = (((4 * peakVelocity) / 100) * peakVelocity) / 100;

          const negativeMin = analysisState.MinSignalIntensities.filter(
            v => v < 0,
          );
          if (negativeMin.length === 0) {
            analysisState.MeanPressureGradient = 0;
          }
          else {
            const meanVelocitySquared
              = negativeMin.reduce((sum, v) => sum + v * v, 0)
                / negativeMin.length;
            analysisState.MeanPressureGradient
              = (4 * meanVelocitySquared) / 100 / 100;
          }
        }

        const accelerations: number[] = [];
        for (let i = 1; i < analysisState.FlowRates.length; i++) {
          const deltaT
            = analysisState.TriggerTimes[i] - analysisState.TriggerTimes[i - 1]
              || 1;
          accelerations.push(
            (analysisState.FlowRates[i] - analysisState.FlowRates[i - 1])
            / deltaT,
          );
        }
        analysisState.MaximumAcceleration = Math.max(...accelerations);
        analysisState.MinimumAcceleration = Math.min(...accelerations);

        analysisState.HeartRate = heartRate;
        analysisState.VolPerMin
          = (dominantVolume * analysisState.HeartRate) / 1000;
        analysisState.VolPerMinEffective
          = (analysisState.TotalVolume * analysisState.HeartRate) / 1000;
      }

      return analysisState;
    },
    setOverlayNoiseThreshold(value: number) {
      if (this.overlayNoiseThreshold === value) return;
      this.overlayNoiseThreshold = value;
      this.resetOverlayData();
    },
    addOverlayData(sopInstanceUID: string, pixelData: Uint8Array) {
      useCurrentStudyStore().addOverlayVtkImageData(sopInstanceUID, pixelData);
    },
    removeOverlayData(sopInstanceUID: string) {
      useCurrentStudyStore().removeOverlayVtkImageData(sopInstanceUID);
    },
    resetOverlayData() {
      const store = useCurrentStudyStore();
      const { seriesIdx } = this.seriesIndices;
      if (seriesIdx < 0) return;

      const series = store.activeStudy.series[seriesIdx];
      const sliceImages = series?.slices?.[1]?.images;
      if (!sliceImages) return;

      sliceImages.forEach((image) => {
        store.removeOverlayVtkImageData(image.uid);
      });
    },
    setOverlayDataForAllPhases() {
      const { seriesIdx } = this.seriesIndices;
      if (seriesIdx < 0) return;
      const series = useCurrentStudyStore().activeStudy.series[seriesIdx];
      const targetImages = series?.slices?.[1]?.images;
      if (!targetImages) return;
      for (let i = 0; i < targetImages.length; i++) {
        this.setOverlayData(i);
      }
    },
    setOverlayData(phaseIndex: number) {
      const { seriesIdx } = this.seriesIndices;
      if (seriesIdx < 0) return;

      const series = useCurrentStudyStore().activeStudy.series[seriesIdx];
      const vencImages = series?.slices?.[0]?.images;
      const targetImages = series?.slices?.[1]?.images;

      if (!vencImages || !targetImages) return;
      if (
        phaseIndex < 0
        || phaseIndex >= vencImages.length
        || phaseIndex >= targetImages.length
      ) {
        return;
      }

      const vencUID = vencImages[phaseIndex].uid;
      const targetUID = targetImages[phaseIndex].uid;
      if (!vencUID || !targetUID) return;
      this.generateOverlayData(targetUID, vencUID);
    },
    generateOverlayData(
      sopInstanceUID: string,
      vencSopInstanceUID: string,
    ): void {
      if (!sopInstanceUID || !vencSopInstanceUID) return;

      const vencVtkImageData
        = useCurrentStudyStore().getVtkImageData(vencSopInstanceUID);
      const magnitudeVtkImageData
        = useCurrentStudyStore().getVtkImageData(sopInstanceUID);
      if (!vencVtkImageData) return;

      const dims = vencVtkImageData.getDimensions();
      const w = dims[0];
      const h = dims[1];
      const scalars = vencVtkImageData.getPointData().getScalars();
      if (!scalars) return;

      const vencData = scalars.getData();
      const numPixels = w * h;
      const rgba = new Uint8Array(numPixels * 4);

      // Find absolute max value across all pixels
      const range = scalars.getRange();
      const absMax = Math.max(Math.abs(range[0]), Math.abs(range[1]));

      const magnitudeScalars = magnitudeVtkImageData
        ?.getPointData()
        .getScalars();
      if (!magnitudeScalars) return;
      const magnitudeData = magnitudeScalars.getData();
      // const magnitudeRange = magnitudeScalars.getRange();
      // const magnitudeAbsMax = Math.max(Math.abs(magnitudeRange[0]), Math.abs(magnitudeRange[1]));

      if (absMax === 0) return;

      const threshold = (this.overlayNoiseThreshold / 100) * absMax;
      const alpha = Math.round(0.8 * 255);

      for (let i = 0; i < numPixels; i++) {
        const value = vencData[i];
        const absValue = Math.abs(value);
        if (absValue >= threshold) {
          const magnitudeValue = magnitudeData[i];
          const magnitudeAbsValue = Math.abs(magnitudeValue);
          if (magnitudeAbsValue >= 15) {
            const idx = i * 4;
            const intensity = Math.min(
              Math.round((absValue / absMax) * 255),
              255,
            );
            const fadeRange = Math.abs(threshold - absMax);
            const fadeFactor = Math.min(fadeRange / absMax, 1);
            const pixelAlpha = Math.round(alpha * fadeFactor);

            if (value > 0) {
              rgba[idx] = intensity;
              rgba[idx + 1] = 0;
              rgba[idx + 2] = 0;
            }
            else {
              rgba[idx] = 0;
              rgba[idx + 1] = 0;
              rgba[idx + 2] = intensity;
            }
            rgba[idx + 3] = pixelAlpha;
          }
        }
      }
      this.addOverlayData(sopInstanceUID, rgba);
    },
  },
});
